/* eslint-disable no-nested-ternary */
import { FollowUp, OralHealthReportSubmit } from '@brands/Dashboard/ConsultationPage/assets/types';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import { UserProfile } from '@brands/services/identity/types/UserProfile';
import { ObjectType, updateOneReportObject } from '@brands/services/oneReport/updateOneReportObject';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { checkCollapsibleHeaderColor } from '@brands/Utils/getCollapsibleHeader';
import {
  careCoordinationTypeOptions,
  careCoordinationUrgencyOptions,
  oralHealthCoachingTypeOptions,
} from '@brands/Utils/selectOptions';
import { MoreHorizOutlined } from '@mui/icons-material';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { Divider, Menu, MenuItem } from '@mui/material';
import Button from '@mui/material/Button';
import React, { useState } from 'react';
import { FieldErrors } from 'react-hook-form';

import styles from '../../styles.module.scss';
import Person from '../PossibleTreatmentNeedsSection/icons/person.svg';
import FollowUpModal, { FollowUpFormData } from './modal/FollowUpModal';
import Modal, { Styles } from 'react-modal';
import { config } from '@brands/config/config';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import AddFollowUpCareSection from '@brands/Dashboard/ConsultationPage/Components/AddFollowUpCareSection';
import { deletePendingLinkedCase } from '@brands/services/cases/multiStage';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { caseTypeLabel } from '@brands/Dashboard/MultiStage/multiStageUi';

const pickerModalStyles: Styles = {
  ...customStylesDashboard,
  overlay: {
    ...customStylesDashboard.overlay,
    overflowY: 'auto',
  },
  content: {
    ...customStylesDashboard.content,
    width: '612px',
    maxWidth: 'calc(100vw - 32px)',
    boxSizing: 'border-box',
    padding: '28px 44px 36px',
    maxHeight: '88vh',
    overflowY: 'auto',
    alignItems: 'stretch',
  },
};

type FollowUpSectionProps = {
  consultationForm: OralHealthReportSubmit;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  userInfo: UserProfile;
  errors: FieldErrors<OralHealthReportSubmit>;
  journeyPaused?: boolean;
  journeyAtLimit?: boolean;
};

const FollowUpSection = ({
  consultationForm,
  setConsultationForm,
  thisCase,
  setCase,
  userInfo,
  errors,
  journeyPaused = false,
  journeyAtLimit = false,
}: FollowUpSectionProps): JSX.Element => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState<FollowUp | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [removingPending, setRemovingPending] = useState(false);
  const isMultiStage = config.features.multiStage;
  const followUpAddressed = isMultiStage && thisCase.follow_up_addressed === true;
  const followUpEnabled = thisCase.organization?.follow_up_enabled === true;
  // The single deferred follow-up selection saved on the case (materialized
  // into a linked case only when the report is submitted).
  const pendingLinkedType = isMultiStage ? thisCase.pending_linked_case_type : undefined;
  const hasPendingLinked = Boolean(pendingLinkedType);

  const handleRemovePending = async (): Promise<void> => {
    setRemovingPending(true);
    try {
      await deletePendingLinkedCase(Number(thisCase.id));
      const refreshed = await getOneCase(Number(thisCase.id));
      setCase(refreshed);
    } catch (err) {
      displayErrorDetails(err);
    } finally {
      setRemovingPending(false);
    }
  };

  const followUpError = typeof errors.followUp?.message === 'string' ? errors.followUp.message : '';
  const hasFollowUpError = Boolean(followUpError);

  const canEdit = checkCollapsibleHeaderColor(thisCase || '', userInfo, '') === 'edit';
  // Whoever may add follow-up care may also change or remove it, so both share
  // one gate. The unnamed-tab gate above resolves to read-only for care
  // advisors, who own the care-coordination and oral-health-coaching
  // consultations.
  const canEditFollowUpCare = checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Education') === 'edit';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleSaveFollowUp = async (data: FollowUpFormData) => {
    const payload: Partial<FollowUp> = {
      care_coordination: data.careCoordination ?? null,
      care_coordination_type: data.careCoordination ? data.careType : '',
      care_coordination_urgency: data.careCoordination ? data.careUrgency : '',
      care_coordination_reason: data.careCoordination ? data.careDescription : '',
      oral_health_coaching: data.oralCoaching ?? null,
      oral_health_coaching_type: data.oralCoaching ? data.oralType : '',
      oral_health_coaching_urgency: data.oralCoaching ? data.oralUrgency : '',
      oral_health_coaching_reason: data.oralCoaching ? data.oralDescription : '',
    };

    const id = thisCase.one_report?.follow_ups?.id || '1';

    try {
      const res = await updateOneReportObject(thisCase?.one_report.id || '', ObjectType.FollowUp, {
        ...payload,
        id,
      });

      setConsultationForm((prev) => ({
        ...prev,
        followUp: res.follow_ups,
      }));
    } catch (err) {
      displayErrorDetails(err);
    }

    setModalOpen(false);
    setEditingFollowUp(null);
  };

  const handleEditFollowUp = () => {
    setEditingFollowUp(consultationForm?.followUp || null);
    setModalOpen(true);
    handleMenuClose();
  };

  const hasAnsweredCare =
    consultationForm?.followUp?.care_coordination === true ||
    (consultationForm?.followUp?.care_coordination === false &&
      consultationForm?.followUp?.care_coordination_type !== 'DO-2870');

  const hasAnsweredOral =
    consultationForm?.followUp?.oral_health_coaching === true ||
    (consultationForm?.followUp?.oral_health_coaching === false &&
      consultationForm?.followUp?.oral_health_coaching_type !== 'DO-2870');

  // Only a persisted record — which always carries an id — counts as an answer.
  // The consultation form is not hydrated from the case for care-coordination
  // and oral-health-coaching cases, so it keeps its initial default (both flags
  // `false`, empty types), which otherwise reads as a saved "Not required" pair
  // and renders a static summary in place of the picker.
  const hasFollowUpData = consultationForm?.followUp?.id != null && (hasAnsweredCare || hasAnsweredOral);

  if (!followUpEnabled) {
    return <></>;
  }

  return (
    <div
      className={`${styles.possibleTreatmentNeedsSectionDivChild} ${
        hasFollowUpData ? styles.possibleTreatmentNeedsSectionDivChildExist : ''
      } ${hasFollowUpError ? styles.followUpSectionError : ''}`}
    >
      <div className={styles.possibleTreatmentNeedsSectionDivChildRow}>
        <span className={styles.possibleTreatmentNeedsSectionDivChildFollowUp}>
          <img src={Person} alt="person icon" />
        </span>

        {isMultiStage && hasPendingLinked ? (
          <div className={styles.existDataDiv}>
            <span>Follow-up care: {caseTypeLabel(pendingLinkedType as string)}</span>
            {canEditFollowUpCare && (
              <span>
                <Button variant="text" onClick={() => setPickerOpen(true)} disabled={journeyPaused}>
                  Edit
                </Button>
                <Button variant="text" onClick={handleRemovePending} disabled={journeyPaused || removingPending}>
                  {removingPending ? 'Removing…' : 'Remove'}
                </Button>
              </span>
            )}
          </div>
        ) : !hasFollowUpData ? (
          <Button
            variant="text"
            onClick={() => {
              if (isMultiStage) {
                setPickerOpen(true);
                return;
              }
              setEditingFollowUp(null);
              setModalOpen(true);
            }}
            className={`${styles.followUp} ${hasFollowUpError && !followUpAddressed ? styles.followUpButtonError : ''}`}
            disabled={journeyPaused || !canEditFollowUpCare || journeyAtLimit}
          >
            {journeyAtLimit
              ? 'Add follow-up care (limit reached)'
              : journeyPaused || followUpAddressed
                ? 'Add follow-up care'
                : 'Add follow-up care (required)'}
          </Button>
        ) : (
          <div className={styles.existDataDiv}>
            <span>Follow-up care</span>
            {canEdit && <MoreHorizOutlined onClick={(e: any) => handleMenuOpen(e)} style={{ cursor: 'pointer' }} />}
          </div>
        )}
      </div>

      {hasFollowUpData && (
        <div className={styles.followUpBody}>
          <Divider orientation="horizontal" flexItem className={styles.divider} />

          {consultationForm?.followUp?.care_coordination === true && (
            <div className={styles.followUpDetails}>
              <div className={styles.followUpDetailsHeader}>
                <div className={styles.followUpDetailsHeaderTitle}>
                  <span className={styles.followUpDetailsHeaderTitleSpan}>Care Coordination</span>
                </div>

                <span className={styles.followUpDetailsHeaderTitlePersonCheckSpan}>
                  <HowToRegIcon /> Patient requested
                </span>
              </div>

              <div className={styles.followUpDetailsBody}>
                <div className={styles.followUpDetailsBodyChild}>
                  <span className={styles.followUpDetailsBodyChildHead}>Type</span>
                  <span className={styles.followUpDetailsBodyChildValue}>
                    {careCoordinationTypeOptions.find(
                      (o) => o.value === consultationForm?.followUp?.care_coordination_type,
                    )?.label || consultationForm?.followUp?.care_coordination_type}
                  </span>
                </div>

                <Divider orientation="vertical" flexItem className={styles.divider} />

                <div className={styles.followUpDetailsBodyChild}>
                  <span className={styles.followUpDetailsBodyChildHead}>Urgency</span>
                  <span className={styles.followUpDetailsBodyChildValue}>
                    {careCoordinationUrgencyOptions.find(
                      (o) => o.value === consultationForm?.followUp?.care_coordination_urgency,
                    )?.label || consultationForm?.followUp?.care_coordination_urgency}
                  </span>
                </div>
              </div>

              <div className={styles.followUpDetailsReason}>
                <span className={styles.followUpDetailsBodyChildHead}>Reason</span>
                <span className={styles.followUpDetailsBodyChildValue}>
                  {consultationForm?.followUp?.care_coordination_reason}
                </span>
              </div>
            </div>
          )}

          {hasAnsweredCare && consultationForm?.followUp?.care_coordination === false && (
            <div className={styles.followUpDetails}>
              <div className={styles.followUpDetailsHeader}>
                <div className={styles.followUpDetailsHeaderTitle}>
                  <span className={styles.followUpDetailsHeaderTitleSpan}>Care Coordination</span>
                </div>
              </div>
              <span className={styles.followUpDetailsBodyChildValue}>Not required</span>
            </div>
          )}

          {consultationForm?.followUp?.oral_health_coaching === true && (
            <div className={styles.followUpDetails}>
              <div className={styles.followUpDetailsHeader}>
                <div className={styles.followUpDetailsHeaderTitle}>
                  <span className={styles.followUpDetailsHeaderTitleSpan}>Oral Health Coaching</span>
                </div>
              </div>

              <div className={styles.followUpDetailsBody}>
                <div className={styles.followUpDetailsBodyChild}>
                  <span className={styles.followUpDetailsBodyChildHead}>Type</span>
                  <span className={styles.followUpDetailsBodyChildValue}>
                    {oralHealthCoachingTypeOptions.find(
                      (o) => o.value === consultationForm?.followUp?.oral_health_coaching_type,
                    )?.label || consultationForm?.followUp?.oral_health_coaching_type}
                  </span>
                </div>

                <Divider orientation="vertical" flexItem className={styles.divider} />

                <div className={styles.followUpDetailsBodyChild}>
                  <span className={styles.followUpDetailsBodyChildHead}>Urgency</span>
                  <span className={styles.followUpDetailsBodyChildValue}>
                    {careCoordinationUrgencyOptions.find(
                      (o) => o.value === consultationForm?.followUp?.oral_health_coaching_urgency,
                    )?.label || consultationForm?.followUp?.oral_health_coaching_urgency}
                  </span>
                </div>
              </div>

              <div className={styles.followUpDetailsReason}>
                <span className={styles.followUpDetailsBodyChildHead}>Reason</span>
                <span className={styles.followUpDetailsBodyChildValue}>
                  {consultationForm.followUp?.oral_health_coaching_reason}
                </span>
              </div>
            </div>
          )}

          {hasAnsweredOral && consultationForm?.followUp?.oral_health_coaching === false && (
            <div className={styles.followUpDetails}>
              <div className={styles.followUpDetailsHeader}>
                <div className={styles.followUpDetailsHeaderTitle}>
                  <span className={styles.followUpDetailsHeaderTitleSpan}>Oral Health Coaching</span>
                </div>
              </div>
              <span className={styles.followUpDetailsBodyChildValue}>Not required</span>
            </div>
          )}

          <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={handleEditFollowUp}>Edit</MenuItem>
          </Menu>
        </div>
      )}

      {hasFollowUpError && !followUpAddressed && <div className={styles.sectionErrorText}>{followUpError}</div>}

      <FollowUpModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingFollowUp(null);
        }}
        onSave={handleSaveFollowUp}
        initialData={editingFollowUp as FollowUp | null}
      />
      {isMultiStage && (
        <Modal
          isOpen={pickerOpen}
          onRequestClose={() => setPickerOpen(false)}
          ariaHideApp={false}
          style={pickerModalStyles}
        >
          <AddFollowUpCareSection
            thisCase={thisCase}
            setCase={setCase}
            disabled={journeyPaused}
            onClose={() => setPickerOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default React.memo(FollowUpSection);
